import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  Clock,
  ClipboardList,
  MessageCircle,
  CheckCheck,
  Inbox,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  getNotifications,
  markNotificationAsRead,
} from "../../utils/firebasefunctions";
import { useStateValue } from "../../context/StateProvider";
import { urlForNotification } from "../../utils/notificationRouting";
import PageHeader from "../PageHeader";
import { SkeletonListItem } from "../Skeleton";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "unread", label: "Unread" },
];

function MainNotifications() {
  const [{ user }] = useStateValue();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");

  useEffect(() => {
    if (!user?.id) return;
    let active = true;
    (async () => {
      try {
        const data = await getNotifications(user.id);
        if (!active) return;
        // Newest first.
        const sorted = [...(data || [])].sort(
          (a, b) =>
            Number(b?.notificationid || 0) - Number(a?.notificationid || 0),
        );
        setNotifications(sorted);
      } catch (err) {
        console.error("Failed to load notifications:", err);
        toast.error("Could not load notifications");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [user?.id]);

  const visible = useMemo(() => {
    if (activeFilter === "unread") {
      return notifications.filter((n) => n.read !== true);
    }
    return notifications;
  }, [notifications, activeFilter]);

  const unreadCount = notifications.filter((n) => n.read !== true).length;

  const handleOpen = async (n) => {
    try {
      if (!n.read) {
        await markNotificationAsRead(n.id);
        setNotifications((prev) =>
          prev.map((item) =>
            item.id === n.id ? { ...item, read: true } : item,
          ),
        );
      }
      const url = urlForNotification(n);
      if (url) navigate(url);
    } catch (err) {
      console.error("Notification click error:", err);
    }
  };

  const handleMarkAllRead = async () => {
    const unread = notifications.filter((n) => n.read !== true);
    if (unread.length === 0) return;
    try {
      await Promise.all(unread.map((n) => markNotificationAsRead(n.id)));
      setNotifications((prev) =>
        prev.map((item) => ({ ...item, read: true })),
      );
      toast.success("All marked as read");
    } catch (err) {
      console.error("Mark all read failed:", err);
      toast.error("Could not mark all as read");
    }
  };

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      <PageHeader
        title="Notifications"
        subtitle={unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
        actions={
          <button
            type="button"
            onClick={handleMarkAllRead}
            disabled={unreadCount === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            <CheckCheck size={14} />
            Mark all read
          </button>
        }
      />

      <div className="flex-shrink-0 px-6 pt-3 pb-2 bg-white border-b border-slate-200">
        <div className="flex gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setActiveFilter(f.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                activeFilter === f.id
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-900 hover:text-slate-900"
              }`}
            >
              {f.label}
              {f.id === "unread" && unreadCount > 0 && (
                <span className="ml-1.5 opacity-70">({unreadCount})</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 lg:px-6 py-4">
        {loading ? (
          <ul className="space-y-2 max-w-3xl mx-auto">
            {Array.from({ length: 5 }).map((_, i) => (
              <li
                key={i}
                className="bg-white rounded-lg border border-slate-200"
              >
                <SkeletonListItem />
              </li>
            ))}
          </ul>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Inbox size={28} />
            <p className="text-sm mt-2">
              {activeFilter === "unread"
                ? "No unread notifications."
                : "No notifications yet."}
            </p>
          </div>
        ) : (
          <ul className="space-y-2 max-w-3xl mx-auto">
            {visible.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => handleOpen(n)}
                  className={`w-full text-left flex items-start gap-3 px-4 py-3 rounded-lg border transition ${
                    n.read
                      ? "bg-white border-slate-200 hover:border-slate-300"
                      : "bg-indigo-50/50 border-indigo-200 hover:border-indigo-300"
                  }`}
                >
                  <NotificationIcon type={n.type} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-3">
                      <h4
                        className={`text-sm truncate ${
                          n.read
                            ? "text-slate-700 font-medium"
                            : "text-slate-900 font-semibold"
                        }`}
                      >
                        {n.notificationtitle || "Notification"}
                      </h4>
                      <span className="text-xs text-slate-400 flex-shrink-0">
                        {n.notificationid
                          ? moment(Number(n.notificationid)).fromNow()
                          : ""}
                      </span>
                    </div>
                    {n.notificationmessage && (
                      <p className="text-sm text-slate-600 mt-0.5 line-clamp-2">
                        {n.notificationmessage}
                      </p>
                    )}
                  </div>
                  {!n.read && (
                    <span className="size-2 rounded-full bg-indigo-500 mt-2 flex-shrink-0" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function NotificationIcon({ type }) {
  const wrap =
    "size-8 rounded-full flex items-center justify-center flex-shrink-0";
  if (type === "deadline") {
    return (
      <div className={`${wrap} bg-red-50 text-red-500`}>
        <Clock size={16} />
      </div>
    );
  }
  if (type === "mention") {
    return (
      <div className={`${wrap} bg-blue-50 text-blue-600`}>
        <MessageCircle size={16} />
      </div>
    );
  }
  return (
    <div className={`${wrap} bg-slate-100 text-slate-600`}>
      <ClipboardList size={16} />
    </div>
  );
}

export default MainNotifications;
